import { useCallback, useEffect, useMemo, useRef } from "react";
import { Animated, PanResponder, type LayoutChangeEvent } from "react-native";

// PE-27 fetta 1 — the card's horizontal swipe flips the WEEK.
//
// ⭐ Why the week and not the exercise (Antenore, design locked 28/08): the
// three axes get one control each, and no gesture may duplicate another's job.
// The roster picks the EXERCISE, the grid picks the DAY, and flipping the weeks
// of a fixed exercise IS editing its progression — Squat W1 → W2 → W3 is the
// thing being authored, so it is the thing the card sfoglia.
//
// ⭐⭐ PE-31 (Antenore, smoke 29/08) — IT HAS TO FEEL LIKE A PHOTO GALLERY.
// Sue parole: «deve essere come lo slider animazione/muscle map, come in una
// galleria foto, si fa il slide con il dito, non con una frecciolina». The
// first cut only LISTENED for a swipe: nothing moved under the thumb until
// 64px had gone by, and then the week changed in one jump — so the control
// that actually read as the week-changer was `PagerNav`'s arrow beside it.
// That arrow is gone; this hook is now the whole gesture:
//   · the card FOLLOWS the finger from the first ~12px,
//   · it resists at the two ends instead of pretending there is a page there,
//   · release commits on distance OR on a flick's speed, and the new week
//     slides in from the side the finger came from.
// ⛔ The counter did not follow the arrow into the bin — it moved into the
// card's action bar, where it is also the screen reader's door onto the axis
// (a swipe reaches neither VoiceOver nor a keyboard). See SupersetTabs.
//
// ⛔ Still not a paged ScrollView, and the reason is unchanged: that is what
// `usePagerScroll`/`ExerciseVisualPager` are, and they render one page per
// item. The card here is DERIVED from the selected week, so N pages would be N
// cards of the same exercise, each with its own inputs and modal state. What
// moves is a cursor; the animation is a single card sliding out and back in.
// ⛔ And not react-native-gesture-handler: web-rnw only tolerates it, it does
// not use it (vite.config stubs __DEV__ to stop it crashing on load).
// PanResponder is core RN and RNW implements it.
//
// The gesture POLICY, split from the responder that runs it: how far sideways
// is a swipe, how straight must it be, and where does it land. It lives out
// here because PanResponder derives its dx/dy from a touch history no test can
// fake honestly — so the test asserts the policy, and the responder below is
// the two lines that hand it real numbers.

/** Sideways travel before the deck takes the gesture off the vertical scroll. */
const CLAIM_DX = 12;
/** …and how much straighter than tall it has to be to be a sideways one. */
const CLAIM_RATIO = 2;
/** Past this much travel, the release commits whatever the speed. */
const COMMIT_DX = 56;
/** …and under it, a fast flick commits anyway — the gallery's own rule. */
const COMMIT_VX = 0.3;
/** How much of the drag survives past the first/last week: enough to feel the
 *  wall, not enough to look like a page is coming. */
const EDGE_RESISTANCE = 0.25;

/** Is this drag a week swipe at all — i.e. should the deck start following the
 *  finger? Deliberately eager: a gallery moves with the thumb, so the question
 *  here is only "is this sideways", not yet "is this enough to turn a page".
 *  The card scrolls vertically, and a thumb travelling down a column of sets
 *  crosses a few pixels sideways every time — hence the straightness ratio. */
export function isWeekSwipe(dx: number, dy: number, weekCount: number) {
  return (
    weekCount > 1 &&
    Math.abs(dx) > CLAIM_DX &&
    Math.abs(dx) > CLAIM_RATIO * Math.abs(dy)
  );
}

/** Where the swipe lands, or null when it lands nowhere — either it never
 *  earned a page turn, or the page it asks for is past the first/last week.
 *  `vx` is the release velocity: a short, fast flick is a page turn in every
 *  gallery, and requiring the full distance from it is what makes a deck feel
 *  stuck. */
export function weekAfterSwipe(
  dx: number,
  week: number,
  weekCount: number,
  vx = 0,
) {
  const committed = Math.abs(dx) > COMMIT_DX || Math.abs(vx) > COMMIT_VX;
  if (!committed || dx === 0) return null;
  // Drag left = go forward, the direction the content would travel.
  const next = dx < 0 ? week + 1 : week - 1;
  return next >= 0 && next < weekCount ? next : null;
}

/** The live offset the card is drawn at while the finger is down. Past the
 *  first or last week there is nothing to reveal, so the card only gives a
 *  quarter of the travel — the wall you feel in every photo gallery. */
export function resistedDx(dx: number, week: number, weekCount: number) {
  const target = dx < 0 ? week + 1 : week - 1;
  const exists = target >= 0 && target < weekCount;
  return exists ? dx : dx * EDGE_RESISTANCE;
}

export function useWeekSwipe({
  week,
  weekCount,
  onSelectWeek,
  reducedMotion,
}: {
  week: number;
  weekCount: number;
  onSelectWeek: (week: number) => void;
  /** ⛔ Chi ha chiesto al sistema di RIDURRE IL MOVIMENTO cambia settimana
   *  lo stesso — sparisce lo scorrimento, non la funzione. Same rule
   *  ExerciseVisualPager applies to its own paging. */
  reducedMotion: boolean;
}) {
  const offset = useRef(new Animated.Value(0)).current;
  // The card's own width, measured — the slide-out distance has to be the real
  // one or the card reappears before it has left. Never the window: on the wide
  // console this tree can sit in a column.
  const widthRef = useRef(0);
  // The handlers are built once, so the live values have to be readable from
  // inside them without rebuilding the responder on every week change.
  const state = useRef({ week, weekCount, onSelectWeek, reducedMotion });
  state.current = { week, weekCount, onSelectWeek, reducedMotion };
  // ⛔ The commit slide is 140ms during which the card can be unmounted — the
  // tab switches to Matrice, or Back leaves the editor. The animation's
  // completion callback fires anyway, and it would then move the week cursor of
  // a tree nobody is looking at (standards review). ⭐ It is not a crash, which
  // is exactly why it would never have been noticed.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    widthRef.current = e.nativeEvent.layout.width;
  }, []);

  const panHandlers = useMemo(() => {
    const settle = (animated: boolean) => {
      if (!animated) {
        offset.setValue(0);
        return;
      }
      Animated.spring(offset, {
        toValue: 0,
        useNativeDriver: true,
        bounciness: 0,
        speed: 16,
      }).start();
    };

    return PanResponder.create({
      // ⛔ Never claim on START. The card is full of controls — set rows,
      // steppers, the effort band — and a parent that claims the touch down
      // swallows all of them. Claiming on MOVE lets any child that wants the
      // gesture take it first; what reaches here is what nothing else wanted.
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_e, g) =>
        isWeekSwipe(g.dx, g.dy, state.current.weekCount),
      onPanResponderMove: (_e, g) => {
        const { week: w, weekCount: n } = state.current;
        offset.setValue(resistedDx(g.dx, w, n));
      },
      onPanResponderRelease: (_e, g) => {
        const {
          week: w,
          weekCount: n,
          onSelectWeek: go,
          reducedMotion: rm,
        } = state.current;
        const next = weekAfterSwipe(g.dx, w, n, g.vx);
        if (next === null) {
          settle(!rm);
          return;
        }
        if (rm) {
          offset.setValue(0);
          go(next);
          return;
        }
        // Out the way the thumb was going, then the NEW week — the card is
        // derived, so changing the cursor is what redraws it — starts off the
        // opposite edge and springs home. One card, two halves of one slide.
        const outward = g.dx < 0 ? -1 : 1;
        const span = widthRef.current || COMMIT_DX * 6;
        Animated.timing(offset, {
          toValue: outward * span,
          duration: 140,
          useNativeDriver: true,
        }).start(({ finished }) => {
          // ⛔ `finished` is not ceremony. A second swipe starting while this
          // one is still sliding out calls `offset.setValue` from the move
          // handler, which INTERRUPTS this animation — and the callback still
          // fires. Without the guard the week would change under a thumb that
          // is already dragging somewhere else (consolidation review).
          if (!finished || !alive.current) return;
          go(next);
          offset.setValue(-outward * span);
          settle(true);
        });
      },
      // A cancelled touch (a phone call, a parent taking the responder) must
      // not leave the card parked halfway off its own screen.
      onPanResponderTerminate: () => settle(false),
    }).panHandlers;
  }, [offset]);

  return {
    panHandlers,
    onLayout,
    style: { transform: [{ translateX: offset }] },
  };
}
